/**
 * Утилиты для проверки прав доступа пользователей
 */

/**
 * Получает информацию о текущем пользователе из localStorage
 */
export const getCurrentUser = () => {
    try {
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser) {
            return JSON.parse(currentUser);
        }
    } catch (e) {
        console.error('Ошибка получения текущего пользователя:', e);
    }
    return null;
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// --- Дедупликация запроса списка саппов ---------------------------------
// Раньше при каждой загрузке страницы список саппов (/api/files/saps/public)
// тянулся 2-3 раза подряд: отдельно для роли, отдельно для прав, ещё раз в
// AdminPanel. Все — с ?t=Date.now(), т.е. без кэша браузера. Это давало
// последовательный «водопад» одинаковых запросов.
//
// Теперь запрос выполняется ОДИН раз и переиспользуется:
//  - in-flight dedup: параллельные вызовы ждут один и тот же промис;
//  - короткий TTL: повторные вызовы в пределах окна берут готовый результат.
// Роль/права меняются редко, а живой контроль сессии идёт отдельным путём
// (checkUserSession), поэтому короткий TTL здесь безопасен.
let _sapsCache = null;       // { ts, saps }
let _sapsInflight = null;    // Promise<saps[]>
const SAPS_TTL_MS = 15000;

const fetchSapsList = () => {
    const now = Date.now();
    if (_sapsCache && (now - _sapsCache.ts) < SAPS_TTL_MS) {
        return Promise.resolve(_sapsCache.saps);
    }
    if (_sapsInflight) return _sapsInflight;
    _sapsInflight = (async () => {
        try {
            const response = await fetch('/api/files/saps/public', {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                const saps = Array.isArray(data.saps) ? data.saps : [];
                _sapsCache = { ts: Date.now(), saps };
                return saps;
            }
        } catch (e) {
            console.error('Ошибка загрузки списка саппов:', e);
        } finally {
            _sapsInflight = null;
        }
        return _sapsCache ? _sapsCache.saps : [];
    })();
    return _sapsInflight;
};

// Сбрасывает кэш саппов — вызывать после изменения ролей/прав, чтобы
// свежие данные подтянулись немедленно, не дожидаясь TTL.
export const invalidateSapsCache = () => {
    _sapsCache = null;
    _sapsInflight = null;
};

// Находит запись текущего пользователя в списке саппов
const findMySap = (saps) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return null;
    return (saps || []).find(s => {
        const sapId = String(s.id || '');
        const userId = String(currentUser.id || '');
        const sapUsername = (s.username || '').toLowerCase();
        const userUsername = (currentUser.username || '').toLowerCase();
        return (sapId === userId || sapId === currentUser.id || s.id === userId) &&
               sapUsername === userUsername;
    }) || null;
};

/**
 * Получает роль текущего пользователя из БД
 */
export const getCurrentUserRole = async () => {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser) return null;
        const saps = await fetchSapsList();
        const sap = findMySap(saps);
        return sap?.role || 'sap';
    } catch (e) {
        console.error('Ошибка получения роли пользователя:', e);
    }
    return null;
};

/**
 * Проверяет, имеет ли пользователь доступ к странице настроек (/settings)
 */
export const canAccessSettings = (role) => {
    // Админ и Тимлид сапов имеют полный доступ
    return role === 'admin' || role === 'teamlead';
};

/**
 * Проверяет, имеет ли пользователь доступ к SiteSettings
 */
export const canAccessSiteSettings = (role) => {
    // Админ, Тимлид сапов и Доверенный имеют доступ
    return role === 'admin' || role === 'teamlead' || role === 'trusted';
};

/**
 * Проверяет, может ли пользователь видеть определенный таб в SiteSettings
 */
export const canAccessSiteSettingsTab = (role, tabName) => {
    // Админ и Тимлид сапов видят все табы
    if (role === 'admin' || role === 'teamlead') {
        return true;
    }
    
    // Доверенный видит только кошельки и финансы
    if (role === 'trusted') {
        return tabName === 'wallets' || tabName === 'finance';
    }
    
    // Сап не имеет доступа к SiteSettings вообще
    return false;
};

/**
 * Получает список доступных табов для пользователя в SiteSettings
 */
export const getAvailableTabs = (role) => {
    if (role === 'admin' || role === 'teamlead') {
        return ['general', 'finance', 'wallets', 'banks', 'translations', 'miniFigma'];
    }
    
    if (role === 'trusted') {
        return ['wallets', 'finance'];
    }
    
    return [];
};

/**
 * Получает список разрешенных конфигов для текущего пользователя
 */
export const getAllowedConfigs = async () => {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser) return null;

        const saps = await fetchSapsList();
        const sap = findMySap(saps);

        // Админ видит все конфиги (возвращаем null как индикатор "все")
        if (sap?.role === 'admin') {
            return null; // null означает "все конфиги"
        }
        // Для остальных возвращаем массив разрешенных конфигов
        return sap?.allowedConfigs || [];
    } catch (e) {
        console.error('Ошибка получения разрешенных конфигов:', e);
    }
    return [];
};

